import { Loader2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type ProductInferredFact,
  useDeleteProductInferredFact,
} from '@/shared/hooks/api/product'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

type ProductInferredFactDeleteDialogProps = {
  productId: string
  fact: ProductInferredFact | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProductInferredFactDeleteDialog({
  productId,
  fact,
  open,
  onOpenChange,
}: ProductInferredFactDeleteDialogProps) {
  const { t } = useTranslation(['productCatalog', 'common'])
  const { mutateAsync: deleteInferredFact, isPending } =
    useDeleteProductInferredFact()

  const factLabel = fact?.label.trim() || t('productCatalog:detail.fields.notSet')

  async function onDelete() {
    if (fact == null) {
      return
    }
    try {
      await deleteInferredFact({
        productId,
        factId: fact.id,
      })
      toast.success(t('productCatalog:detail.inferredFacts.deleteDialog.success'))
      onOpenChange(false)
    } catch {
      toast.error(t('productCatalog:detail.inferredFacts.deleteDialog.error'))
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('productCatalog:detail.inferredFacts.deleteDialog.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('productCatalog:detail.inferredFacts.deleteDialog.description', {
              label: factLabel,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <Button
            type='button'
            variant='destructive'
            onClick={() => {
              void onDelete()
            }}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className='me-2 size-4 animate-spin' aria-hidden />
                {t('productCatalog:detail.inferredFacts.deleteDialog.deleting')}
              </>
            ) : (
              <>
                <Trash2 className='me-2 size-4' aria-hidden />
                {t('productCatalog:detail.inferredFacts.deleteDialog.confirm')}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
